import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { categoriaService, Categoria } from '../../services/categoriaService';
import { useAuthStore } from '../../store/authStore';
import CategoriaForm from '../../components/Categorias/CategoriaForm';

const CategoriasList = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState<Categoria | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const { data: categorias, isLoading } = useQuery(
    ['categorias', user?.tenantId],
    () => categoriaService.getAll(user?.tenantId)
  );

  const deleteMutation = useMutation(categoriaService.delete, {
    onSuccess: () => {
      toast.success('Categoría eliminada correctamente');
      queryClient.invalidateQueries(['categorias']);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al eliminar categoría');
    }
  });

  const handleDelete = (id: number) => {
    if (window.confirm('¿Estás seguro de eliminar esta categoría?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleEdit = (categoria: Categoria) => {
    setEditingCategoria(categoria);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingCategoria(null);
  };

  const toggleExpand = (id: number) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedCategories(newExpanded);
  };

  const renderCategoria = (categoria: Categoria, level = 0) => {
    const hasChildren = categoria.children && categoria.children.length > 0;
    const isExpanded = expandedCategories.has(categoria.id);

    return (
      <div key={categoria.id} className={`${level > 0 ? 'ml-8 mt-2' : ''}`}>
        <div className="flex items-center justify-between p-3 bg-white border rounded hover:bg-gray-50">
          <div className="flex items-center space-x-3">
            {hasChildren && (
              <button
                onClick={() => toggleExpand(categoria.id)}
                className="text-gray-500 hover:text-gray-700"
              >
                {isExpanded ? '▼' : '▶'}
              </button>
            )}
            {!hasChildren && <span className="w-4" />}
            <span className="font-medium">{categoria.name}</span>
            {categoria.description && (
              <span className="text-sm text-gray-500">- {categoria.description}</span>
            )}
            <span className={`px-2 py-1 text-xs rounded-full ${categoria.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {categoria.isActive ? 'Activa' : 'Inactiva'}
            </span>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handleEdit(categoria)}
              className="text-primary-600 hover:text-primary-900 text-sm"
            >
              Editar
            </button>
            <button
              onClick={() => handleDelete(categoria.id)}
              className="text-red-600 hover:text-red-900 text-sm"
            >
              Eliminar
            </button>
          </div>
        </div>
        {hasChildren && isExpanded && (
          <div className="mt-2">
            {categoria.children?.map((child) => renderCategoria(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return <div className="text-center py-8">Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Categorías</h1>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition"
        >
          + Nueva Categoría
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        {categorias && categorias.length > 0 ? (
          <div className="space-y-2">
            {categorias.map((categoria) => renderCategoria(categoria))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No hay categorías. Crea una nueva categoría para comenzar.
          </div>
        )}
      </div>

      {showForm && (
        <CategoriaForm
          categoria={editingCategoria}
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
};

export default CategoriasList;

